import { useQuery } from '@tanstack/react-query';
import { AdminService } from 'app/API';
import { cancelOnSignal } from 'app/API/core/CancelablePromise';
import { adminUsersKeys } from 'app/queries/adminUsers/adminUsersKeys';
import type { TCall } from 'app/queries/types/TCall';

export const useAdminUserDetailsQueries = (call: TCall, userId: string) => {
  const enabled = Boolean(userId);
  const profile = useQuery({
    queryKey: adminUsersKeys.detail(userId),
    queryFn: async ({ signal }) => {
      const response = await call(() => cancelOnSignal(AdminService.getUserDetails({ userId }), signal));
      if (!response.success || !response.result) {
        throw new Error(response.error ?? 'Unable to load user details');
      }
      return response.result;
    },
    enabled,
  });
  const organizations = useQuery({
    queryKey: adminUsersKeys.domain(userId, 'organizations'),
    queryFn: ({ signal }) =>
      call(() => cancelOnSignal(AdminService.getAdminUserOrganizationRelations({ userId }), signal), {
        dontRedirectOnError: true,
      }),
    enabled,
  });
  const teams = useQuery({
    queryKey: adminUsersKeys.domain(userId, 'teams'),
    queryFn: ({ signal }) =>
      call(() => cancelOnSignal(AdminService.getAdminUserTeams({ userId }), signal), { dontRedirectOnError: true }),
    enabled,
  });
  const workspaces = useQuery({
    queryKey: adminUsersKeys.domain(userId, 'workspaces'),
    queryFn: ({ signal }) =>
      call(() => cancelOnSignal(AdminService.getAdminUserAccessibleWorkspaces({ userId }), signal), {
        dontRedirectOnError: true,
      }),
    enabled,
  });
  const domains = { organizations, teams, workspaces };
  const user = profile.data
    ? {
        ...profile.data,
        orgRelations: organizations.data ?? [],
        teams: teams.data ?? [],
        workspaces: workspaces.data ?? [],
      }
    : undefined;
  return {
    user,
    isLoading: profile.isLoading || Object.values(domains).some((query) => query.isLoading),
    hasInitialError: profile.isError && profile.data === undefined,
    isFetching: profile.isFetching || Object.values(domains).some((query) => query.isFetching),
    failedDomains: Object.entries(domains).filter(([, query]) => query.isError),
    refetch: () => Promise.all([profile.refetch(), ...Object.values(domains).map((query) => query.refetch())]),
  };
};
